import { createContext, useContext } from "react";
import { Lock, Eye } from "lucide-react";
import { toast } from "sonner";

// { environment: 'preview'|'production', isProduction: bool }
export const AdminEnvContext = createContext({ environment: "preview", isProduction: false });

export const AdminEnvProvider = ({ environment, children }) => {
  const value = {
    environment: environment || "preview",
    isProduction: environment === "production",
  };
  return <AdminEnvContext.Provider value={value}>{children}</AdminEnvContext.Provider>;
};

export const useAdminEnv = () => useContext(AdminEnvContext);

// Environment badge shown in the admin header (always visible).
export const EnvBadge = ({ environment }) => {
  const isProd = environment === "production";
  return (
    <span
      data-testid="env-badge"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
        isProd
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-amber-50 text-amber-700 border-amber-200"
      }`}
      title={isProd ? "Live production copy" : "Preview / authoring copy"}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isProd ? "bg-emerald-500" : "bg-amber-500"}`} />
      {isProd ? "Production" : "Preview"}
    </span>
  );
};

// Banner shown on read-only features when viewed in Production.
export const ReadOnlyBanner = ({ feature = "This feature" }) => (
  <div
    data-testid="readonly-banner"
    className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-600"
  >
    <Lock className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
    <div className="text-sm">
      <p className="font-semibold text-slate-700 flex items-center gap-2">
        Read-only in Production
        <span className="inline-flex items-center gap-1 text-xs font-normal text-slate-500">
          <Eye className="h-3.5 w-3.5" /> view only
        </span>
      </p>
      <p className="text-slate-500">
        {feature} is managed in <strong>Preview</strong> and promoted here. Changes made in Preview
        appear in Production after a promotion push.
      </p>
    </div>
  </div>
);

// One-time global fetch wrapper: surfaces a friendly toast whenever the server rejects a
// write/run action because the feature is read-only in Production. Keeps every mutating
// control effectively inert without touching each component.
let _installed = false;
export const installReadOnlyInterceptor = () => {
  if (_installed || typeof window === "undefined" || !window.fetch) return;
  _installed = true;
  const orig = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const res = await orig(...args);
    if (res.status === 403) {
      try {
        const clone = res.clone();
        const data = await clone.json();
        if (data && data.read_only) {
          toast.error("Read-only in Production", {
            description:
              "This change must be made in Preview and promoted. Production is a live, read-only mirror for authoring features.",
          });
        }
      } catch (_) {
        /* non-JSON 403s are ignored */
      }
    }
    return res;
  };
};
